import React, { useEffect, useRef, useMemo } from 'react';
import { defineAdSlot, displayAdSlot, destroyAdSlot, onSlotRenderEnded } from '@/utils/gpt';
import type { GoogleAdsPlacement } from '@/hooks/useTenant';
import { needsTallFrame, TALL_CREATIVE_ATTR } from '@/utils/tallCreatives';

export interface AdSlotProps {
  divId: string;
  adPlacement: GoogleAdsPlacement;
  className?: string;
  placement?: string;
  /**
   * Called once GAM has answered for this slot, with whether it came back empty
   * (no ad sold). Lets a call site put something else in the space rather than
   * leave a reserved-but-blank block on screen.
   */
  onRenderEnded?: (isEmpty: boolean) => void;
  /**
   * Height to hold open before a creative arrives. Defaults to the tallest offered
   * size, which is right for a slot that must not reflow; pass the shortest instead
   * where an over-reserved slot would show as dead space. Either way this is only a
   * floor — the container has no maximum, so a taller creative grows it and is never
   * clipped.
   */
  minHeightPx?: number;
}

/**
 * AdSlot component for rendering Google Ad Manager ads
 * Squares sticky footer: 320x50 (mobile) per squares_requirements.md
 */
export const AdSlot: React.FC<AdSlotProps> = ({
  divId,
  adPlacement,
  className = '',
  placement = 'sticky-footer',
  onRenderEnded,
  minHeightPx,
}) => {
  const adUnitPath = useMemo(() => adPlacement.adUnitPath || '', [adPlacement.adUnitPath]);
  const sizes = useMemo(
    () =>
      adPlacement.sizes || [
        [320, 50],
        [728, 90],
      ],
    [adPlacement.sizes]
  );
  const sizeMappings = useMemo(() => adPlacement.sizeMappings, [adPlacement.sizeMappings]);
  const targeting = useMemo(() => adPlacement.targeting, [adPlacement.targeting]);
  const isDefinedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /*
   * Held in a ref so the subscription below is keyed on `divId` alone. Call sites
   * pass an inline arrow, and re-subscribing on every render would risk missing the
   * one `slotRenderEnded` this slot ever fires.
   */
  const onRenderEndedRef = useRef(onRenderEnded);
  onRenderEndedRef.current = onRenderEnded;

  useEffect(() => {
    return onSlotRenderEnded(divId, (isEmpty, event) => {
      /*
       * Grant the taller iOS frame only to creatives known to paint taller than they
       * are served (see utils/tallCreatives.ts). Set per fill, so a refresh that swaps
       * a tall creative for a normal one drops the dead band with it.
       */
      const container = containerRef.current;
      if (container) {
        if (!isEmpty && needsTallFrame(event)) container.setAttribute(TALL_CREATIVE_ATTR, '1');
        else container.removeAttribute(TALL_CREATIVE_ATTR);
      }
      onRenderEndedRef.current?.(isEmpty);
    });
  }, [divId]);

  useEffect(() => {
    if (!isDefinedRef.current) {
      defineAdSlot({
        adUnitPath,
        sizes,
        divId,
        targeting,
        sizeMappings,
      });
      isDefinedRef.current = true;
    }
    displayAdSlot(divId);

    return () => {
      if (isDefinedRef.current) {
        destroyAdSlot(divId);
        isDefinedRef.current = false;
      }
    };
  }, [divId, adUnitPath, sizes, sizeMappings, targeting]);

  const maxHeight = Math.max(...sizes.map(s => s[1]));
  const maxWidth = Math.max(...sizes.map(s => s[0]));
  const reservedHeight = minHeightPx ?? maxHeight;

  return (
    <div
      ref={containerRef}
      data-test-id={`ad-slot-${placement}`}
      className={`ad-slot-container relative w-full flex items-center justify-center min-h-[50px] ${className}`}
      // A floor, never a cap: a creative taller than this grows the box rather than
      // being clipped by it.
      style={{ minHeight: `${reservedHeight}px` }}
    >
      <div
        style={{
          width: `${maxWidth}px`,
          maxWidth: '100%',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div id={divId} className="ad-slot" />
      </div>
    </div>
  );
};

export default AdSlot;
